import { Injectable } from '@angular/core';
import { EmployeeRole, Permission } from '../models/AuthUser';

export interface NavigationItem {
  label: string;
  route: string;
  icon: string;
  requiredPermissions: Permission[];
  children?: NavigationItem[];
  externalUrl?: string; // Optional: open external (backend) links
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly navigationItems: NavigationItem[] = [
    {
      label: 'Dashboard',
      route: 'admin',
      icon: 'dashboard',
      requiredPermissions: [Permission.VIEW_DASHBOARD],
    },
    {
      label: 'Kundenverwaltung',
      route: 'customer-management',
      icon: 'manage_accounts',
      requiredPermissions: [Permission.MANAGE_CUSTOMERS],
      children: [
        {
          label: 'Registrierungen',
          route: 'admin/pending-customers',
          icon: 'pending_actions',
          requiredPermissions: [Permission.MANAGE_CUSTOMERS]
        },
        {
          label: 'Kundensuche',
          route: 'admin/search-customers',
          icon: 'search',
          requiredPermissions: [Permission.MANAGE_CUSTOMERS]
        },
        {
          label: 'Ausstehende Aufträge',
          route: 'pending-orders',
          icon: 'pending_actions',
          requiredPermissions: [Permission.MANAGE_ORDERS],
        }
      ]
    },
    // {
    //   label: 'Administration',
    //   route: 'administration',
    //   icon: 'admin_panel_settings',
    //   requiredPermissions: [Permission.MANAGE_SYSTEM],
    //   children: [
    //     {
    //       label: 'Admin Dashboard',
    //       route: 'admin',
    //       icon: 'dashboard',
    //       requiredPermissions: [Permission.MANAGE_SYSTEM]
    //     },
    //     {
    //       label: 'FAQs verwalten',
    //       route: 'admin/manage-faqs',
    //       icon: 'help',
    //       requiredPermissions: [Permission.MANAGE_SYSTEM]
    //     },
    //     {
    //       label: 'Coin-Staffelung %-Werte',
    //       route: 'coins/manage',
    //       icon: 'account_balance_wallet',
    //       requiredPermissions: [Permission.MANAGE_SYSTEM]
    //     },
    //     {
    //       label: 'Swagger',
    //       route: '/swagger',
    //       icon: 'integration_instructions',
    //       requiredPermissions: [Permission.MANAGE_SYSTEM],
    //       externalUrl: '/swagger'
    //     }
    //   ]
    // },
    // Admin-only group at the end
    {
      label: 'Administration',
      route: 'administration',
      icon: 'admin_panel_settings',
      requiredPermissions: [Permission.MANAGE_SYSTEM],
      children: [
        {
          label: 'FAQs verwalten',
          route: 'admin/manage-faqs',
          icon: 'help',
          requiredPermissions: [Permission.MANAGE_SYSTEM]
        },
        {
          label: 'Coin-Staffelung',
          route: 'admin/coins/manage',
          icon: 'account_balance_wallet',
          requiredPermissions: [Permission.MANAGE_SYSTEM]
        },
        {
          label: 'Token Information',
          route: 'employee/token-info',
          icon: 'security',
          requiredPermissions: [Permission.MANAGE_SYSTEM]
        },
        {
          label: 'Admin Test',
          route: 'admin/test',
          icon: 'bug_report',
          requiredPermissions: [Permission.MANAGE_SYSTEM]
        },
        {
          label: 'Swagger',
          route: '/swagger',
          icon: 'integration_instructions',
          requiredPermissions: [Permission.MANAGE_SYSTEM],
          externalUrl: '/swagger'
        }
      ]
    }
  ];

  /**
   * Get navigation items filtered by user permissions
   */
  getNavigationForUser(userPermissions: Permission[]): NavigationItem[] {
    return this.filterNavigationByPermissions(
      this.navigationItems,
      userPermissions
    );
  }

  /**
   * Filter navigation items based on user permissions
   */
  private filterNavigationByPermissions(
    items: NavigationItem[],
    userPermissions: Permission[]
  ): NavigationItem[] {
    return items
      .filter((item) =>
        this.hasRequiredPermissions(item.requiredPermissions, userPermissions)
      )
      .map((item) => ({
        ...item,
        children: item.children
          ? this.filterNavigationByPermissions(item.children, userPermissions)
          : undefined,
      }))
      .filter((item) => !item.children || item.children.length > 0); // Remove parent items with no accessible children
  }

  /**
   * Check if user has required permissions for navigation item
   */
  private hasRequiredPermissions(
    requiredPermissions: Permission[],
    userPermissions: Permission[]
  ): boolean {
    return requiredPermissions.every((permission) =>
      userPermissions.includes(permission)
    );
  }

  /**
   * Get quick access items for dashboard based on role
   */
  getQuickAccessForRole(role: EmployeeRole): NavigationItem[] {
    const quickAccessMap: Record<EmployeeRole, NavigationItem[]> = {
      [EmployeeRole.ADMINISTRATOR]: [
        {
          label: 'User Management',
          route: 'admin/users',
          icon: 'manage_accounts',
          requiredPermissions: [Permission.MANAGE_USERS],
        },
        {
          label: 'System Health',
          route: 'admin/health',
          icon: 'monitor_heart',
          requiredPermissions: [Permission.MANAGE_SYSTEM],
        },
        {
          label: 'Reports',
          route: 'reports',
          icon: 'analytics',
          requiredPermissions: [Permission.VIEW_REPORTS],
        },
      ],
      [EmployeeRole.HEAD_OF]: [
        {
          label: 'Orders',
          route: 'orders',
          icon: 'shopping_cart',
          requiredPermissions: [Permission.MANAGE_ORDERS],
        },
        {
          label: 'Customers',
          route: 'customers',
          icon: 'people',
          requiredPermissions: [Permission.VIEW_CUSTOMER_DATA],
        },
        {
          label: 'Reports',
          route: 'reports',
          icon: 'analytics',
          requiredPermissions: [Permission.VIEW_REPORTS],
        },
      ],
      [EmployeeRole.SALES]: [
        {
          label: 'Customers',
          route: 'customers',
          icon: 'people',
          requiredPermissions: [Permission.VIEW_CUSTOMER_DATA],
        },
        {
          label: 'Support Tickets',
          route: 'support',
          icon: 'support_agent',
          requiredPermissions: [Permission.MANAGE_SUPPORT_TICKETS],
        },
      ],
      [EmployeeRole.READ_ONLY]: [
        {
          label: 'Dashboard',
          route: '',
          icon: 'dashboard',
          requiredPermissions: [Permission.VIEW_DASHBOARD],
        },
      ],
    };

    return quickAccessMap[role] || [];
  }
}
